// lib/footfall/planDeck.ts
// A Plan scenario as a Slides presentation: the merchandising plan laid out as the landlord's own plans are (units
// outlined, vacancies in the landlord's purple with their code and area, the proposals marked, tenant logos in their
// units, the centre's and the landlord's logos, "plan non contractuel"), then the evidence behind it, then the
// shortlist it draws on. Pure: the client draws the plan image and hands its placements over.
import type { SlideDeckDoc, SlideDeckOrigin, SlideElement, SlidePageDoc } from '#types/types';

/** The landlord's purple (Altarea), the colour its own plans give vacancies. */
export const PLAN_PURPLE = '#5A2A82';
export const PLAN_PROPOSAL = '#E9A13B';
const INK = '#1A1A1A', GREY = '#6B6B6B', RULE = '#D9D9D9';
/** 4:3, the landlord's own plan format. */
export const PLAN_PAGE = { width: 720, height: 540 } as const;

export interface PlanDeckLevel {
  /** "Niveau 0". */
  label: string;
  imageUrl: string;
  /** Width / height of the drawn plan. */
  aspect: number;
  /** Logos to place, in fractions of the drawn plan (0..1), side as a fraction of its width. */
  logos: Array<{ url: string; name: string; cx: number; cy: number; side: number }>;
}
export interface PlanDeckMove { unit: string; areaSqm: number; brand: string; rentPerSqm: number | null; uplift: number | null; kind: 'relet' | 'vacate' }
export interface PlanDeckInput {
  fr: boolean;
  origin: SlideDeckOrigin;
  centreLogoUrl: string | null;
  landlord: { name: string; logoUrl: string | null };
  thesis: string;
  totals: { valueCreated: number | null; uplift: number | null; occupancyToday: string; occupancyScenario: string; works: number };
  assumptions: { referenceRent: number | null; yieldPct: number | null };
  levels: PlanDeckLevel[];
  moves: PlanDeckMove[];
  layout: string[];
  shortlist: { label: string; total: number; rows: Array<{ name: string; logoUrl: string | null; trade: string; fit: number; size: string; placed: boolean }> };
}

const euro = (v: number | null, fr: boolean) => v === null ? '–' : new Intl.NumberFormat(fr ? 'fr-FR' : 'en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

/** Builds the presentation deck of a scenario. Element ids are stable within the deck. */
export function buildPlanDeck(input: PlanDeckInput): SlideDeckDoc {
  const { fr } = input;
  let n = 0;
  const id = (p: string) => `${p}-${++n}`;
  const text = (x: number, y: number, w: number, h: number, value: string, o: { size?: number; bold?: boolean; color?: string; align?: 'left' | 'center' | 'right'; lineHeight?: number } = {}): SlideElement =>
    ({ id: id('t'), type: 'text', frame: { x, y, w, h }, text: value, fontSize: o.size ?? 10, fontWeight: o.bold ? 'bold' : 'normal', align: o.align ?? 'left', color: o.color ?? INK, fontFamily: 'sans', lineHeight: o.lineHeight ?? 1.25 });
  const image = (x: number, y: number, w: number, h: number, url: string): SlideElement => ({ id: id('img'), type: 'image', frame: { x, y, w, h }, url });
  // A solid swatch is a patch (a rect's fill is a 10% tint); an outlined one, for 'let', is a rect without fill.
  const box = (x: number, y: number, w: number, h: number, color: string, fill = true): SlideElement => fill
    ? { id: id('p'), type: 'patch', frame: { x, y, w, h }, background: color }
    : { id: id('r'), type: 'rect', frame: { x, y, w, h }, color, fill: false, strokeWidth: 1 };
  const { width: W, height: H } = PLAN_PAGE;
  // Every page carries the centre's logo top left and the landlord's top right, as the landlord's plans do.
  const brand = (): SlideElement[] => [
    ...(input.centreLogoUrl ? [image(14, 10, 52, 52, input.centreLogoUrl)] : []),
    ...(input.landlord.logoUrl ? [image(W - 52, 14, 38, 38, input.landlord.logoUrl)] : []),
  ];
  const heading = (title: string, sub: string): SlideElement[] => [text(76, 16, W - 150, 22, title, { size: 16, bold: true }), text(76, 38, W - 150, 16, sub, { size: 9, color: GREY })];
  // No snapshotUrl: Slides paints a page's snapshot as its background, which would show the plan twice.
  const page = (index: number, label: string, elements: SlideElement[]): SlidePageDoc =>
    ({ id: id('page'), index, kind: 'blank', width: W, height: H, background: '#FFFFFF', label, elements });
  const date = new Date(input.origin.sentAt).toLocaleDateString(fr ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  // ---------- the plan, one page per level the scenario touches ----------
  const planPages = input.levels.map((level, i) => {
    const area = { x: 20, y: 64, w: W - 40, h: H - 112 };
    const w = Math.min(area.w, area.h * level.aspect), h = w / level.aspect;
    const frame = { x: area.x + (area.w - w) / 2, y: area.y + (area.h - h) / 2, w, h };
    const legend: Array<[string, string]> = [[PLAN_PURPLE, fr ? 'Vacant' : 'Vacant'], [PLAN_PROPOSAL, fr ? 'Proposition du scénario' : 'Scenario proposal'], ['#FFFFFF', fr ? 'Loué' : 'Let']];
    return page(i, `${fr ? 'Plan de merchandising' : 'Merchandising plan'} · ${level.label}`, [
      ...brand(),
      ...heading(input.origin.scenarioName, `${input.origin.propertyName} · ${fr ? 'Plan de merchandising' : 'Merchandising plan'} · ${level.label} · ${date}`),
      image(frame.x, frame.y, frame.w, frame.h, level.imageUrl),
      ...level.logos.map(l => { const side = Math.max(10, Math.min(30, l.side * frame.w)); return image(frame.x + l.cx * frame.w - side / 2, frame.y + l.cy * frame.h - side / 2, side, side, l.url); }),
      ...legend.flatMap(([colour, label], j) => [box(24 + j * 150, H - 40, 12, 12, colour === '#FFFFFF' ? GREY : colour, colour !== '#FFFFFF'), text(42 + j * 150, H - 41, 120, 14, label, { size: 9 })]),
      text(W - 260, H - 24, 240, 12, fr ? 'Plan non contractuel. Susceptible de modifications.' : 'Non-contractual plan. Subject to change.', { size: 7, color: GREY, align: 'right' }),
    ]);
  });

  // ---------- why: the figures, the reasoning, the moves ----------
  const t = input.totals;
  const metrics: Array<[string, string]> = [
    [fr ? 'Valeur créée' : 'Value created', euro(t.valueCreated, fr)],
    [fr ? 'Hausse de loyer / an' : 'Rent uplift / yr', euro(t.uplift, fr)],
    [fr ? 'Occupation' : 'Occupancy', `${t.occupancyToday} → ${t.occupancyScenario}`],
    [fr ? 'Travaux' : 'Works', euro(t.works, fr)],
  ];
  const mw = (W - 48 - 3 * 8) / 4;
  const moveRows = input.moves.slice(0, 14);
  const rowH = 17;
  const cols = [0, 52, 172, 222, 280];
  const whyPage = page(planPages.length, fr ? 'Justification' : 'Rationale', [
    ...brand(),
    ...heading(fr ? 'Pourquoi ce scénario' : 'Why this scenario', `${input.origin.scenarioName} · ${input.origin.planName}`),
    ...metrics.flatMap(([label, value], i) => [box(24 + i * (mw + 8), 70, mw, 52, '#F4F1F8'), text(34 + i * (mw + 8), 78, mw - 20, 12, label, { size: 8, color: GREY }), text(34 + i * (mw + 8), 94, mw - 20, 20, value, { size: 14, bold: true, color: PLAN_PURPLE })]),
    text(24, 138, 330, 14, fr ? 'Le raisonnement' : 'The reasoning', { size: 11, bold: true }),
    text(24, 156, 330, H - 200, input.thesis || (fr ? 'Raisonnement non rédigé.' : 'No reasoning written.'), { size: 8.5, lineHeight: 1.35 }),
    text(376, 138, 320, 14, fr ? 'Les mouvements' : 'The moves', { size: 11, bold: true }),
    ...[fr ? 'Cellule' : 'Unit', fr ? 'Enseigne' : 'Brand', 'm²', '€/m²/an', fr ? 'Hausse / an' : 'Uplift / yr'].map((h, i) => text(376 + cols[i]!, 158, 60, 12, h, { size: 7.5, color: GREY, align: i >= 2 ? 'right' : 'left' })),
    box(376, 172, 320, 0.6, RULE),
    ...moveRows.flatMap((m, r) => {
      const y = 176 + r * rowH;
      return [
        text(376, y, 50, 12, m.unit, { size: 8 }),
        text(376 + cols[1]!, y, 116, 12, m.kind === 'vacate' ? (fr ? 'Libérer' : 'Vacate') : m.brand, { size: 8, bold: m.kind === 'relet' }),
        text(376 + cols[2]!, y, 44, 12, Math.round(m.areaSqm).toLocaleString(fr ? 'fr-FR' : 'en-GB'), { size: 8, align: 'right' }),
        text(376 + cols[3]!, y, 52, 12, m.rentPerSqm === null ? '–' : String(Math.round(m.rentPerSqm)), { size: 8, align: 'right' }),
        text(376 + cols[4]!, y, 40, 12, m.uplift === null ? '–' : euro(m.uplift, fr), { size: 8, align: 'right' }),
      ];
    }),
    ...(input.moves.length > moveRows.length ? [text(376, 176 + moveRows.length * rowH, 320, 12, `+ ${input.moves.length - moveRows.length} ${fr ? 'autres mouvements' : 'more moves'}`, { size: 8, color: GREY })] : []),
    ...(input.layout.length ? [text(376, H - 104, 320, 40, `${fr ? 'Restructuration' : 'Reshaping'} : ${input.layout.join(' ; ')}`, { size: 8 })] : []),
    text(24, H - 34, W - 48, 22, `${fr ? 'Hypothèses' : 'Assumptions'} : ${fr ? 'loyer de référence' : 'reference rent'} ${input.assumptions.referenceRent ?? '–'} €/m²/${fr ? 'an' : 'yr'}, ${fr ? 'taux de rendement' : 'yield'} ${input.assumptions.yieldPct ?? '–'} %. ${fr ? 'Valeur stabilisée, non actualisée ; loyers cibles proposés, non signés.' : 'Stabilised value, not discounted; proposed target rents, not signed.'}`, { size: 7, color: GREY }),
  ]);

  // ---------- the shortlist it draws on ----------
  const list = [...input.shortlist.rows.filter(r => r.placed), ...input.shortlist.rows.filter(r => !r.placed)].slice(0, 18);
  const sCols = [0, 26, 230, 380, 440];
  const shortlistPage = page(planPages.length + 1, fr ? 'Short-list retenue' : 'Retained shortlist', [
    ...brand(),
    ...heading(fr ? 'La short-list retenue' : 'The retained shortlist', `${input.shortlist.label} · ${input.shortlist.total} ${fr ? 'enseignes classées' : 'ranked brands'}`),
    ...['', fr ? 'Enseigne' : 'Brand', fr ? 'Activité' : 'Trade', fr ? 'Note' : 'Fit', fr ? 'Format habituel' : 'Usual size'].map((h, i) => text(24 + sCols[i]!, 72, 140, 12, h, { size: 7.5, color: GREY })),
    box(24, 86, W - 48, 0.6, RULE),
    ...list.flatMap((r, i) => {
      const y = 92 + i * 22;
      return [
        ...(r.logoUrl ? [image(24, y - 2, 18, 18, r.logoUrl)] : []),
        text(24 + sCols[1]!, y, 200, 14, r.name, { size: 9, bold: r.placed, color: r.placed ? PLAN_PURPLE : INK }),
        text(24 + sCols[2]!, y, 145, 14, r.trade, { size: 8.5 }),
        text(24 + sCols[3]!, y, 50, 14, String(r.fit), { size: 8.5 }),
        text(24 + sCols[4]!, y, 200, 14, r.size, { size: 8.5 }),
      ];
    }),
    text(24, H - 30, W - 48, 14, fr ? 'En violet : les enseignes que le scénario place. Notes et formats : recherche Source du centre.' : 'In purple: the brands the scenario places. Fit and sizes: the centre\'s Source run.', { size: 7, color: GREY }),
  ]);

  return { kind: 'presentation', origin: input.origin, pages: [...planPages, whyPage, shortlistPage] };
}
